import type { EditSpecDict, EditSpecClipDict, EditSpecSourceRefDict } from '../../../../generated/nodeDataTypes'
import type { TimelineClip, TimelineState } from './types'
import { dreamwriteEditSpecDurationSeconds } from './dreamvibeAdapter'

export function editSpecSourceKey(source: EditSpecSourceRefDict): string {
  return `${source.node_id}.${source.output_port}${source.history_index === undefined ? '' : `@${source.history_index}`}`
}

/** A view of the canonical edit, never a replacement legacy render config. */
export function projectEditSpecTimeline(
  spec: EditSpecDict,
  sources: Readonly<Record<string, string>>,
  view: Pick<TimelineState, 'playheadPosition' | 'zoomLevel' | 'selectedClipIds' | 'isPlaying'>,
  sourceDurations: Readonly<Record<string, number>> = {},
): TimelineState {
  const fps = spec.format.fps
  let endFrame = 0
  const clips: TimelineClip[] = spec.clips.map((clip, order) => {
    const sourceDuration = sourceDurations[editSpecSourceKey(clip.source)] ?? clip.out_seconds
    const speed = clip.speed ?? 1
    const durationFrames = Math.max(1, Math.round((clip.out_seconds - clip.in_seconds) / speed * fps))
    if (order && clip.transition_in && clip.transition_in.type !== 'none') {
      endFrame -= Math.round((clip.transition_in.duration_s ?? 0.4) * fps)
    }
    const startOffset = endFrame / fps
    endFrame += durationFrames
    return {
      id: clip.id, sourceNodeId: clip.source.node_id,
      sourceRef: editSpecSourceKey(clip.source), sourcePath: sources[editSpecSourceKey(clip.source)] ?? '',
      label: clip.id, originalDuration: sourceDuration,
      trimStart: clip.in_seconds, trimEnd: sourceDuration - clip.out_seconds, effectiveDuration: durationFrames / fps,
      order, startOffset, trackIndex: 0, speed, volume: clip.volume ?? 1,
      muted: clip.muted ?? !(spec.audio?.keep_original_audio ?? true),
    }
  })
  const totalDuration = dreamwriteEditSpecDurationSeconds(spec)
  return {
    ...view, clips, totalDuration,
    audioClips: (spec.audio?.tracks ?? []).map((track, trackIndex) => ({
      id: track.id, sourceNodeId: track.source.node_id, sourceRef: editSpecSourceKey(track.source),
      sourcePath: sources[editSpecSourceKey(track.source)] ?? '',
      originalDuration: (track.end_seconds ?? totalDuration) - track.start_seconds,
      effectiveDuration: (track.end_seconds ?? totalDuration) - track.start_seconds,
      trimStart: 0, trimEnd: 0, startOffset: track.start_seconds,
      volume: track.gain ?? 1, muted: track.gain === 0, speed: 1,
      audioKind: track.role === 'voiceover' ? 'speech' : track.role,
      trackIndex, audioFadeInS: track.fade_in_seconds, audioFadeOutS: track.fade_out_seconds,
    })),
    videoTrackCount: 1, audioTrackCount: Math.max(1, spec.audio?.tracks?.length ?? 0),
    copiedClip: null, copiedClipType: null, mutedVideoTracks: new Set(), mutedAudioTracks: new Set(),
    magnetEnabled: true,
  }
}

export function updateEditSpecClip(spec: EditSpecDict, id: string, patch: Partial<EditSpecClipDict>): EditSpecDict {
  if (!spec.clips.some((clip) => clip.id === id)) throw new Error(`Unknown timeline clip: ${id}`)
  const clips = spec.clips.map((clip) => clip.id === id ? { ...clip, ...patch } : clip)
  for (const clip of clips) {
    if (!Number.isFinite(clip.in_seconds) || !Number.isFinite(clip.out_seconds) ||
        clip.in_seconds < 0 || clip.out_seconds <= clip.in_seconds ||
        !Number.isFinite(clip.speed ?? 1) || (clip.speed ?? 1) <= 0) {
      throw new Error(`Invalid trim or speed for clip ${clip.id}`)
    }
  }
  return { ...spec, clips }
}

export function reorderEditSpecClip(spec: EditSpecDict, id: string, order: number): EditSpecDict {
  const index = spec.clips.findIndex((clip) => clip.id === id)
  if (index < 0 || order < 0 || order >= spec.clips.length) throw new Error('Invalid clip reorder')
  const clips = [...spec.clips]
  const [clip] = clips.splice(index, 1)
  clips.splice(order, 0, clip)
  return { ...spec, clips }
}

export function splitEditSpecClip(spec: EditSpecDict, id: string, sourceSeconds: number, newId: string): EditSpecDict {
  const clip = spec.clips.find((item) => item.id === id)
  if (!clip || sourceSeconds <= clip.in_seconds || sourceSeconds >= clip.out_seconds) {
    throw new Error('Split must be inside the selected clip')
  }
  return { ...spec, clips: spec.clips.flatMap((item) => item.id !== id ? [item] : [
    { ...item, out_seconds: sourceSeconds },
    { ...item, id: newId, in_seconds: sourceSeconds, transition_in: { type: 'none' } },
  ]) }
}
